import { useState } from 'react';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface UseStepperAccordionOptions {
  /** Section ids that start pre-completed (e.g. a section pre-validated before the drawer opens). */
  initiallyCompletedSectionIds?: string[];
}

export interface UseStepperAccordionReturn {
  /** The currently open section id, or `null` when `sectionIds` is empty. */
  activeSectionId: string | null;
  /** Whether `goBack` would move to a previous section. */
  canGoBack: boolean;
  /** Ids of sections marked completed so far. */
  completedSectionIds: string[];
  /**
   * Marks `id` completed without changing the active section — the counterpart to
   * `uncompleteSection` for sections a background effect auto-completes from form state (e.g. once
   * all of a section's required fields resolve) rather than from explicit user navigation. Ignores
   * an `id` outside `sectionIds`, mirroring `openSection`'s own defensive membership check.
   */
  completeSection: (id: string) => void;
  /** Moves to the previous section, if any. The section kept its completed mark. */
  goBack: () => void;
  /** Marks the active section completed and advances to the next one, if any. */
  goNext: () => void;
  /** Whether the active section is the last one in `sectionIds`. */
  isLastSection: boolean;
  /** Reopens a section — only accepts an id already in `completedSectionIds`. */
  openSection: (id: string) => void;
  /** Restores the initial state: first non-pre-completed section active, initial completions kept. */
  reset: () => void;
  /** Removes `id` from `completedSectionIds` and makes it the active section (e.g. clearing a selection). */
  uncompleteSection: (id: string) => void;
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

/**
 * Headless navigation state for `StepperAccordion`. Owns which section is active and which ones
 * are completed — the drawer/page wires `goNext`/`goBack` to Next/Back buttons and
 * `openSection` to `StepperAccordion`'s `onActiveSectionChange`.
 *
 * `options.initiallyCompletedSectionIds` lets a caller start with some sections already marked
 * completed (e.g. a section pre-validated before the drawer opens) — the initial active section
 * becomes the first id not in that list, and `reset()` restores this same initial state.
 */
export function useStepperAccordion(
  sectionIds: string[],
  options?: UseStepperAccordionOptions,
): UseStepperAccordionReturn {
  const initiallyCompletedSectionIds = options?.initiallyCompletedSectionIds ?? [];
  const initialActiveId = sectionIds.find((id) => !initiallyCompletedSectionIds.includes(id)) ?? null;
  const [activeSectionId, setActiveSectionId] = useState<string | null>(initialActiveId);
  const [completedSectionIds, setCompletedSectionIds] = useState<string[]>(initiallyCompletedSectionIds);

  const activeIndex = activeSectionId ? sectionIds.indexOf(activeSectionId) : -1;
  const canGoBack = activeIndex > 0;
  const isLastSection = activeIndex !== -1 && activeIndex === sectionIds.length - 1;

  function completeSection(id: string): void {
    if (!sectionIds.includes(id)) return;
    setCompletedSectionIds((current) => (current.includes(id) ? current : [...current, id]));
  }

  function goNext(): void {
    if (activeSectionId === null || isLastSection) return;
    completeSection(activeSectionId);
    setActiveSectionId(sectionIds[activeIndex + 1]);
  }

  function goBack(): void {
    if (!canGoBack) return;
    setActiveSectionId(sectionIds[activeIndex - 1]);
  }

  function openSection(id: string): void {
    if (completedSectionIds.includes(id)) {
      setActiveSectionId(id);
    }
  }

  function reset(): void {
    setActiveSectionId(initialActiveId);
    setCompletedSectionIds(initiallyCompletedSectionIds);
  }

  function uncompleteSection(id: string): void {
    setCompletedSectionIds((current) => current.filter((sectionId) => sectionId !== id));
    setActiveSectionId(id);
  }

  return {
    activeSectionId,
    canGoBack,
    completedSectionIds,
    completeSection,
    goBack,
    goNext,
    isLastSection,
    openSection,
    reset,
    uncompleteSection,
  };
}
